// src/features/leases/components/ActivitySection.jsx
//
// LSE6 — collapsible activity section (Concessions, Critical Dates,
// Documents, Intelligence Ledger). Renders the section chrome and a
// count badge in the header. Body is whatever the caller passes;
// when empty (count === 0) we render a muted empty-state line.
//
// LEASE-TAB-ACCORDION-STANDARDIZE: header now uses DisclosureSection
// (same "▼ Show / ▲ Close" toggle as Score tab's MethodologyAccordion).
// defaultOpen changed to false so all sections start collapsed.
//
// LEASE-TAB-CLEANUP-3: optional `open` and `onToggle` props for
// externally-controlled (accordion Expand all) mode. When both are
// provided the component is controlled; when absent it falls back to
// internal useState (preserves all existing callers unchanged).

import React, { useState } from "react";
import { T } from "../../../app/tokens.js";
import { DisclosureSection } from "../../../app/DisclosureSection.jsx";

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export function ActivitySection({ title, subtitle, count = 0, emptyText = "None yet.", defaultOpen = false, note, children, open: openProp, onToggle: onToggleProp }) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isControlled = openProp !== undefined && onToggleProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const handleToggle = isControlled ? onToggleProp : () => setInternalOpen((v) => !v);

  const titleNode = (
    <>
      <h3 style={{ margin: 0, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        <span>{title}</span>
        {subtitle && (
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwRegular, color: T.mid }}>
            {subtitle}
          </span>
        )}
      </h3>
      <span
        data-testid={`activity-count-${slugify(title)}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 20,
          height: 18,
          padding: "0 6px",
          borderRadius: 999,
          background: count > 0 ? T.blueSoft : T.lightGray,
          color: count > 0 ? T.blue : T.mid,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
        }}
      >
        {count}
      </span>
    </>
  );

  return (
    <section
      data-testid={`activity-section-${slugify(title)}`}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        padding: "10px 16px",
      }}
    >
      <DisclosureSection
        title={titleNode}
        open={open}
        onToggle={handleToggle}
        buttonTestId="activity-section-toggle"
        buttonPadding="0"
      >
        <div style={{ paddingTop: 10 }}>
          {note && <div style={{ paddingBottom: 8 }}>{note}</div>}
          {count === 0
            ? <div style={{ fontSize: T.fsBase, color: T.mid, fontStyle: "italic" }}>{emptyText}</div>
            : children}
        </div>
      </DisclosureSection>
    </section>
  );
}
